/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1734480000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Dec 18 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "customerio",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Retrieve tracking site ID and API key",
          "depth": 1,
          "id": "retrieve-tracking-site-id-and-api-key"
        }, {
          "value": "Retrieve app API key",
          "depth": 1,
          "id": "retrieve-app-api-key"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Customers",
          "depth": 1,
          "id": "customers"
        }, {
          "value": "Objects",
          "depth": 1,
          "id": "objects"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Segments",
          "depth": 1,
          "id": "segments"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      ul: "ul",
      li: "li",
      a: "a",
      img: "img",
      h3: "h3",
      ol: "ol",
      h4: "h4",
      code: "code",
      em: "em"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "You want to provide relevant, timely interactions to your customers. Doing so means that data in your Customer.io account needs to be as accurate and fresh as possible. By syncing customer data directly from your data warehouse into Customer.io, you no longer need to worry about data consistency."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customers"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to customer objects"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source as customer events"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segments"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to new or existing segments"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Objects"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to custom objects"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.p, {
        children: "To give Hightouch access to Customer.io, you need to enter:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "tracking site ID"
        }), "\n", _jsx(_components.li, {
          children: "tracking API key"
        }), "\n", _jsx(_components.li, {
          children: "app API key"
        }), "\n", _jsx(_components.li, {
          children: "the region of your Customer.io account"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Follow the instructions in the ", _jsx(_components.a, {
          href: "#retrieve-tracking-site-id-and-api-key",
          children: "following sections"
        }), " to retrieve your site ID and API keys."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you restrict access to your Customer.io workspaces by IP address, you need to add the ", _jsx(_components.a, {
          href: "/security/networking#ip-addresses",
          children: "Hightouch IP Addresses"
        }), " to your ", _jsx(_components.strong, {
          children: "IP Address Allowlist"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-customerio-ip-allowlist.png",
          alt: "IP Allowlist"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you see \"", _jsx(_components.strong, {
          children: "Any IP address is currently able to access the App API for all workspaces."
        }), "\" in your Customer.io configuration, it means you can skip this step."]
      }), "\n", _jsx(_components.h3, {
        id: "retrieve-tracking-site-id-and-api-key",
        children: "Retrieve tracking site ID and API key"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to your ", _jsx(_components.a, {
            href: "https://fly.customer.io/settings",
            children: "Account Settings"
          }), " and ", _jsx(_components.a, {
            href: "https://fly.customer.io/settings/api_credentials?keyType=tracking",
            children: "Manage API Credentials"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Tracking API Keys"
          }), ", copy a pre-created ", _jsx(_components.strong, {
            children: "Site ID"
          }), " and ", _jsx(_components.strong, {
            children: "API Key"
          }), ", or create a new one to use for Hightouch."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-customerio-tracking-api-key.png",
          alt: "Tracking Credentials Page"
        })
      }), "\n", _jsx(_components.h3, {
        id: "retrieve-app-api-key",
        children: "Retrieve app API key"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to your ", _jsx(_components.a, {
            href: "https://fly.customer.io/settings",
            children: "Account Settings"
          }), " and to the App API Keys tab under the ", _jsx(_components.a, {
            href: "https://fly.customer.io/settings/api_credentials?keyType=app",
            children: "Manage API Credentials"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["Create an ", _jsx(_components.strong, {
            children: "App API Key"
          }), " and assign it to the appropriate workspace, for example \"Hightouch.\""]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-customerio-app-api-key.png",
          alt: "App Credentials Page"
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.h3, {
        id: "customers",
        children: "Customers"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any source to ", _jsx(_components.a, {
          href: "https://www.customer.io/docs/api/#tag/Track-Customers",
          children: "customer objects"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with users in Customer.io on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Customer.io External ID"
        }), "\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "CIO ID"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Customer.io customer attributes. If the customer attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, you have the following options for how Hightouch should handle rows leaving your query results:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the customer in Customer.io"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear Fields"
              })
            }), _jsx(_components.td, {
              children: "Clear the mapped fields but keep the customer record"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete Destination Record"
              })
            }), _jsx(_components.td, {
              children: "Delete the customer record from Customer.io completely"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Update"
        }), " mode, you have the following options for how Hightouch should handle rows leaving your query results:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the customer in Customer.io"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear Fields"
              })
            }), _jsx(_components.td, {
              children: "Clear the mapped fields but keep the customer record"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "objects",
        children: "Objects"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any source to ", _jsx(_components.a, {
          href: "https://docs.customer.io/api/track/#section/Identify-object",
          children: "custom objects"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows in your model with objects in Customer.io on the ", _jsx(_components.code, {
          children: "Object ID"
        }), " field"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Customer.io object attributes. If the object attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h4, {
        id: "relationships",
        children: "Relationships"
      }), "\n", _jsx(_components.p, {
        children: "You can create relationships from each object synced to a customer in Customer.io. You can create relationships to the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Customer.io External ID"
        }), "\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "CIO ID"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior-1",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "You have the following options for how Hightouch should handle rows leaving your query results:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the object in Customer.io"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete destination record"
              })
            }), _jsx(_components.td, {
              children: "Delete the object record from Customer.io completely"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with users in Customer.io on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Customer.io External ID"
        }), "\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "CIO ID"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync the following ", _jsx(_components.a, {
          href: "https://www.customer.io/docs/api/#operation/track",
          children: "customer event"
        }), " types:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Track event"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Track page views"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Track mobile screen views"
          })
        }), "\n"]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsx(_components.p, {
        children: "Provide an event name. This is how you can reference the event in Customer.io campaigns or segments."
      }), "\n", _jsx(_components.h4, {
        id: "event-id-optional",
        children: "Event ID (optional)"
      }), "\n", _jsx(_components.p, {
        children: "Event ID is an identifier Customer.io uses to de-duplicate events. If provided and an event has the same value as previously synced event, Hightouch doesn't process the duplicate event."
      }), "\n", _jsx(_components.h4, {
        id: "user-id",
        children: "User ID"
      }), "\n", _jsx(_components.p, {
        children: "A User ID is a unique value representing a user. You may identify a user by ID, email address, or Customer.io ID."
      }), "\n", _jsxs(_components.p, {
        children: ["If you wish to sync the events as ", _jsx(_components.a, {
          href: "https://www.customer.io/docs/api/#operation/trackAnonymous",
          children: _jsx(_components.em, {
            children: "anonymous"
          })
        }), ", you can skip providing a User ID and provide an Anonymous ID instead."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsx(_components.p, {
        children: "Provide a timestamp of when the event occurred. If this field is empty, Hightouch uses the time the event arrives at the server."
      }), "\n", _jsxs(_components.p, {
        children: ["Any ", _jsx(_components.code, {
          children: "DateTime"
        }), " value provided from your source is converted to Unix timestamp as expected by Customer.io."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Customer.io customer attributes. If the customer attribute doesn't exist, Hightouch creates it."
      }), "\n", _jsx(_components.h3, {
        id: "segments",
        children: "Segments"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any source to ", _jsx(_components.a, {
          href: "https://www.customer.io/docs/api/#tag/Track-Segments",
          children: "new or existing segments"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with users in Customer.io on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Customer.io External ID"
        }), "\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "CIO ID"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "create-a-segment",
        children: "Create a segment"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["To create a new mailing list, select ", _jsx(_components.code, {
            children: "Create a new segment"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "You can provide a name for this segment, or Hightouch use the name of the model used to create the sync."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "use-an-existing-segment",
        children: "Use an existing segment"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["To use an existing mailing list, select ", _jsx(_components.code, {
            children: "Use existing segment"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "You can select any of your existing Customer.io segments from the drop-down select menu."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
